import React, { useState } from 'react';

import { BookingsSearchForm } from '@components/admin/booking/BookingsSearchForm';
import { BookingsTable } from '@components/admin/booking/BookingsTable';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog2 } from '@components/ExportCSVDialog2';
import { adminBookingInquiryTradeCondtionFilters } from '@data/adminBookingInquiry';
import { bookingsStrings } from '@data/bookings';
import { preAlertStringsCN } from '@data/preAlert';
import { useSearch } from '@hooks/next/useSearch';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useUser } from '@hooks/queries/useUser';
import {
  DashboardPreAlertSearchType,
  mapPreAlertStatusLabelToValue,
  type PreAlertType,
} from '@models/next/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { mapOptional } from '@utils/types';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import { pick } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
}));
interface BookingsProps {
  beforeDeparture: boolean;
}

export const BookingsCurrentChina: React.FC<BookingsProps> = (props) => {
  const { beforeDeparture } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(DashboardPreAlertSearchType);

  const { checklist = [], ...preAlertSearch } = search;

  const query = {
    ...preAlertSearch,
    ...(search.trade_condition &&
      adminBookingInquiryTradeCondtionFilters.get(search.trade_condition)),
    is_empty: mapOptional(preAlertSearch.status, mapPreAlertStatusLabelToValue),
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
    before_departure: beforeDeparture,
    type: ['current'] as PreAlertType[],
  };

  const title = bookingsStrings.title()[beforeDeparture ? 'beforeDeparture' : 'afterDeparture'];

  const user = useUser();
  const agencyId = user.data?.agency_id;

  const routePlans = useRoutePlanList({ agency_id: [agencyId!] }, { enabled: !!agencyId });

  const podOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pod)).map((pod) => ({
    value: pod,
    label: pod,
  }));
  const polOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pol)).map((pol) => ({
    value: pol,
    label: pol,
  }));

  const [UIState, setUIState] = useState(() => ({
    exportDialog: {
      open: false,
    },
  }));

  return (
    <Layout title={title} loading={user.isLoading} errors={[user.error]}>
      <ExportCSVDialog2
        open={UIState.exportDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, exportDialog: { open: false } };
          });
        }}
        subject={bookingsStrings.booking}
        title={`${title} ${dayjs().format('YYYY-MM-DD HH:mm')}.csv`}
        baseUrl={'/dashboard/pre-alert/'}
        selected={checklist}
        query={query}
        defaultQuery={pick(query, ['type'])}
      />

      <div className={classes.container}>
        <SectionTitle
          actions={
            <Button
              onClick={() => {
                setUIState((state) => {
                  return { ...state, exportDialog: { open: true } };
                });
              }}
              color="primary"
              text={bookingsStrings.exportCSV}
              shape="round"
            />
          }
        >
          {title}
        </SectionTitle>

        <BookingsSearchForm
          tagFields={[
            'd2d_no',
            'consignee',
            'pol',
            'pod',
            'salesperson_china',
            ...(beforeDeparture ? (['status'] as const) : []),
          ]}
          rangeFields={['eta', 'etd']}
          placeholder={
            <div className={classes.fontChina}>{preAlertStringsCN.tagSearchInputPlaceholder}</div>
          }
          fieldsInputOptions={{
            pol: { type: 'choose', options: polOptions },
            pod: { type: 'choose', options: podOptions },
          }}
        />

        <BookingsTable
          queryParams={query}
          columns={[
            'shipper',
            'pol',
            'pod',
            'cargo_info',
            'etd',
            'vessel',
            'eta',
            ...(beforeDeparture ? (['is_empty'] as const) : []),
            'salesperson_china_last_name',
            'consignee',
          ]}
          user={user.data}
          type="current"
        />
      </div>
    </Layout>
  );
};
